import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  BrowserAuthError,
  BrowserAuthErrorCodes,
  type AccountInfo,
  type Configuration,
  type AuthenticationResult,
} from "@azure/msal-browser";

/**
 * Azure AD B2C sign-in for the SPA.
 *
 * The API (`AddMicrosoftIdentityWebApiAuthentication`) is a bearer-token RESOURCE SERVER: it only
 * validates an `Authorization: Bearer` on each request. This module is the missing CLIENT half —
 * it signs the user into the B2C policy, acquires an access token for the API's scope, and hands it
 * to `api/client.ts` to attach.
 *
 * It is INERT unless configured. All settings come from build-time Vite env vars (see `.env.example`);
 * when `VITE_B2C_CLIENT_ID` is unset (local dev), `isMsalEnabled()` is false and every function
 * no-ops, so the app falls back to the server DevAuth bypass / cookies exactly as before.
 *
 * NOTE: these vars are baked into the bundle at BUILD time — set them on the pipeline's
 * `npm run build` step, not as App Service settings.
 */

import { locale } from "@/shared/i18n";

const clientId = import.meta.env.VITE_B2C_CLIENT_ID;
const authority = import.meta.env.VITE_B2C_AUTHORITY;
const knownAuthority = import.meta.env.VITE_B2C_KNOWN_AUTHORITY;
const apiScope = import.meta.env.VITE_B2C_API_SCOPE;

/**
 * The sign-UP policy's authority (…/B2C_1_SolarConfiguratorSignUp). Optional: unset, `signUp()`
 * falls back to the sign-in authority, which is configured as a sign-up-OR-sign-in policy
 * (`AzureAdB2C:SignUpSignInPolicyId`) and can therefore register a new user on its own. So a
 * missing variable costs a nicer first screen, not a working button.
 */
const signUpAuthority = import.meta.env.VITE_B2C_SIGNUP_AUTHORITY;

/**
 * The password-reset policy's authority (…/B2C_1A_PasswordReset). Optional, but without it a customer
 * who clicks "forgot your password" is returned to the site with an error in the URL and no way onward —
 * B2C ends the sign-in journey on AADB2C90118 expressly so the application can start this one.
 */
const resetAuthority = import.meta.env.VITE_B2C_RESET_AUTHORITY;

/** True when B2C is configured for this build; false in dev → the whole module is a no-op. */
export function isMsalEnabled(): boolean {
  return !!(clientId && authority && apiScope);
}

// The API access-token scope(s). MSAL adds openid/profile/offline_access itself; the API's audience
// check passes only if the token carries THIS custom scope (exposed by the API app registration).
const scopes = apiScope ? [apiScope] : [];

let msal: PublicClientApplication | null = null;

function buildConfig(): Configuration {
  return {
    auth: {
      clientId: clientId!,
      authority: authority!,
      // B2C authorities aren't on login.microsoftonline.com, so they must be allow-listed.
      knownAuthorities: knownAuthority ? [knownAuthority] : [],
      // Must be registered as a "Single-page application" redirect URI on the B2C app registration.
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      // localStorage → the session survives reloads and new tabs (no re-login every refresh).
      cacheLocation: "localStorage",
    },
    system: {
      // How long a silent renewal may hold a request before it is declared dead.
      //
      // acquireTokenSilent falls back to a hidden iframe pointed at B2C whenever it cannot answer from
      // cache, and that iframe cannot carry the B2C session cookie in a browser that partitions
      // third-party cookies — which is now the default one. The renewal does not fail fast when that
      // happens: it waits out this timeout and then reports timed_out or block_iframe_reload.
      //
      // Every API request awaits a token (api/http.ts), so this number is added to the front of each
      // one whenever the silent path is broken. MSAL's own default (DEFAULT_IFRAME_TIMEOUT_MS) is ten
      // seconds, which is ten seconds of a frozen configurator before the request that redraws the
      // door is even sent. Two is enough for an iframe that is going to answer and cheap for one that
      // is not, and the latch below means a visitor pays it once rather than once per click.
      iframeBridgeTimeout: 2000,
    },
  };
}

/**
 * The silent-renewal latch.
 *
 * <p>A hidden-iframe renewal that fails does not fail transiently — the usual cause is the browser
 * declining to send the B2C session cookie into a third-party frame, which is a setting, not a
 * hiccup. Retried per request it is a fixed stall on every call for the rest of the visit, and the
 * answer at the end of the wait is the same one the first attempt already gave.</p>
 *
 * <p>So the first non-interactive failure latches, and calls made while it is set return null
 * immediately — the same value they would have waited for. That is not a decision to sign anybody
 * out: null means "no bearer on this request", and what follows is unchanged. The endpoints the
 * configurator uses are AllowAnonymous and keep answering; anything that requires an identity comes
 * back 401 or with a downgraded X-Session-Principal, and `auth/session` raises the lapsed-sign-in
 * notice from there, as it does for a session that expires any other way.</p>
 *
 * <p>It re-arms after a cooldown so a renewal broken by something that genuinely passes — the network
 * being away for a moment — recovers on its own instead of needing a reload.</p>
 */
const SILENT_RETRY_COOLDOWN_MS = 5 * 60 * 1000;
let silentBlockedUntil = 0;

/** In-flight renewal, shared by every caller that asks while it is running. */
let pending: Promise<string | null> | null = null;

/**
 * Drop the latch. Called where the thing that broke the silent path may have just been fixed: a
 * completed redirect means a fresh B2C session and a token in the cache, so the next request should
 * try rather than sit out the rest of the cooldown.
 */
function resetSilentLatch() {
  silentBlockedUntil = 0;
}

/**
 * Create the MSAL instance and finish any in-flight redirect. Call once at startup BEFORE render.
 * Safe to call when disabled (no-ops) or twice (idempotent).
 */
export async function initMsal(): Promise<void> {
  if (!isMsalEnabled() || msal) return;
  msal = new PublicClientApplication(buildConfig());
  await msal.initialize();

  // Complete a redirect coming back from B2C (the code/token is in the URL hash).
  //
  // Two of B2C's "errors" are not failures, they are the journey asking the application to do the next
  // thing, and an unhandled rejection here leaves the user staring at an error URL on their own site.
  let result: AuthenticationResult | null = null;
  try {
    result = await msal.handleRedirectPromise();
  } catch (error) {
    const description = String((error as { errorMessage?: string })?.errorMessage ?? error);

    // AADB2C90118 - they clicked "forgot your password". Password reset is its own policy, and the
    // sign-in journey ends here precisely so the application can start it.
    if (description.includes("AADB2C90118") && resetAuthority) {
      await msal.loginRedirect({ scopes, authority: resetAuthority, extraQueryParameters: tenantParam() });
      return;
    }

    // AADB2C90091 - they pressed cancel. Nothing went wrong and nothing needs saying; they are simply
    // back where they started, signed out.
    if (!description.includes("AADB2C90091")) throw error;
  }

  if (result?.account) {
    msal.setActiveAccount(result.account);
    // Back from B2C with a fresh session and a token in the cache, so a latch left over from the
    // renewal that sent us there is describing a state that no longer holds.
    resetSilentLatch();
  }

  // Otherwise adopt a cached account (returning user / another tab).
  if (!msal.getActiveAccount()) {
    const [first] = msal.getAllAccounts();
    if (first) msal.setActiveAccount(first);
  }
}

/** The signed-in account, or null (disabled / not signed in). */
export function getAccount(): AccountInfo | null {
  return msal?.getActiveAccount() ?? null;
}

/**
 * Which tenant's branding the B2C sign-in page should wear.
 *
 * One custom policy serves every tenant, so the authority is the same for all of them and the brand
 * travels as a query parameter instead. B2C forwards it (via the policy's ContentDefinitionParameters)
 * to the endpoint that serves the page, which resolves it server-side — the merged page runs on
 * b2clogin.com and cannot read it there.
 *
 * The value is this page's HOSTNAME, which is what decides the tenant server-side everywhere else
 * anyway. The tenant store's id would say the same thing, but not on every path that needs it: the
 * password-reset redirect runs inside `initMsal()`, before the app has mounted and asked the register
 * for anything, so the store still holds its built-in default there and names no tenant that exists.
 * The hostname is available from the first line of script, on every path, and needs no request. It is
 * branding only: the value selects a look, never an identity or a permission.
 *
 * `ui_locales` rides along for the same reason and is read by B2C itself, not by us: it decides which of
 * the policy's supported languages the sign-in form is written in. Without it B2C falls back to the
 * BROWSER's language, so someone who switched the app to Norwegian on an English browser would sign in
 * through an English form and arrive back in a Norwegian app.
 */
function tenantParam(): Record<string, string> {
  return { ui_locales: locale(), tenant: window.location.hostname };
}

/** Redirect to the B2C sign-in page. Navigates away; nothing after it runs. */
export function login(): Promise<void> {
  if (!msal) return Promise.resolve();
  return msal.loginRedirect({ scopes, extraQueryParameters: tenantParam() });
}

/**
 * Redirect to the B2C sign-UP page. Navigates away; nothing after it runs.
 *
 * The same flow as {@link login} with a different policy — which is the whole reason to go through
 * MSAL rather than linking straight at the authorize endpoint. B2C would happily render the form
 * either way and send the browser back with a token in the URL hash, but only `initMsal()`'s
 * `handleRedirectPromise()` reads that hash. Hand-rolled, the user completes registration and lands
 * back on the site signed out, with no account set and no API token acquired.
 *
 * Everything after the redirect is already handled: nothing downstream cares which policy issued
 * the token, so no other part of the auth flow changes.
 */
export function signUp(): Promise<void> {
  if (!msal) return Promise.resolve();
  // Same host as the sign-in authority, so the existing knownAuthorities allow-list covers it.
  const request = { scopes, extraQueryParameters: tenantParam() };
  return msal.loginRedirect(signUpAuthority ? { ...request, authority: signUpAuthority } : request);
}

/** Sign out and clear the cached account. Navigates away. Wire this to a profile/logout button. */
export function logout(): Promise<void> {
  if (!msal) return Promise.resolve();
  return msal.logoutRedirect({ account: msal.getActiveAccount() ?? undefined });
}

/**
 * Silently acquire an API access token for the active account (refreshing as needed). On
 * interaction-required it falls back to a redirect sign-in (navigates away) and returns null.
 * Returns null when disabled or no account — callers then send no bearer (dev / anonymous).
 */
export async function getAccessToken(): Promise<string | null> {
  if (!msal) return null;

  // The ACTIVE account is a pointer MSAL keeps separately from the cached accounts, and it does not
  // always survive — a long-lived tab whose cache cookie was rewritten can end up with accounts still
  // present and no active one. Re-point it rather than reporting no session: returning null here is
  // silent, happens before the try/catch below, and the caller then sends the request unauthenticated
  // and gets a 401 that names nothing.
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
  if (!account) return null;
  if (msal.getActiveAccount() !== account) msal.setActiveAccount(account);

  // Renewal is known broken and the cooldown has not run out — answer with what the wait would have
  // returned. See the latch above for why this is a bounded wait and not a sign-out.
  if (Date.now() < silentBlockedUntil) return null;

  // A step that changes several things at once fires its requests together, and each one asking
  // independently means several iframes racing to renew the same token — the failures then cost the
  // timeout in parallel and the successes throw away all but one result. One renewal, shared.
  if (pending) return pending;

  pending = acquireSilently(msal, account).finally(() => {
    pending = null;
  });
  return pending;
}

async function acquireSilently(
  client: PublicClientApplication,
  account: AccountInfo,
): Promise<string | null> {
  try {
    const res = await client.acquireTokenSilent({ account, scopes });
    // It works — so any latch set by an earlier failure is describing something that has since been
    // fixed, and must not outlive it.
    resetSilentLatch();
    return res.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      // Carries the brand and the language like every other trip to B2C. This is the one that catches
      // people out: it fires when a session has quietly expired, so it is the sign-in page a RETURNING
      // customer sees — and without these it is the only one that would come back in the default brand,
      // in whatever language their browser prefers.
      await client.acquireTokenRedirect({ scopes, extraQueryParameters: tenantParam() });
      return null;
    }
    // block_iframe_reload says the iframe came back from B2C to our redirect URI carrying nothing.
    // B2C was reached and declined to issue a token, which it does when there is no session behind
    // the account — the account record has outlived the sign-in it describes. Retrying cannot change
    // that, and neither can waiting: the record is wrong, so drop it.
    if (e instanceof BrowserAuthError && e.errorCode === BrowserAuthErrorCodes.blockIframeReload) {
      if (await forget(client, account)) return null;
      // Falling through on a failed clear, so a record we could not drop still stops being asked
      // about on every request.
    }

    // Everything else, timed_out chief among them. That one is ambiguous — a renewal that would have
    // succeeded over a slower network reports it too — so the account is kept and only the asking
    // stops. Discarding it here would sign out a dealer whose connection hiccuped, silently, with no
    // way back but a fresh sign-in.
    silentBlockedUntil = Date.now() + SILENT_RETRY_COOLDOWN_MS;
    console.warn(
      "[auth] silent token renewal unavailable; requests continue without a bearer for the next " +
        `${SILENT_RETRY_COOLDOWN_MS / 60000} min`,
      e,
    );
    return null;
  }
}

/**
 * Drop a cached account whose sign-in is provably gone, and report whether it went.
 *
 * <p>clearCache removes the account and its tokens from this browser and nothing else — no redirect,
 * no trip to B2C, no end-session anywhere. There is no session left to end, which is what
 * block_iframe_reload established; this discards our note of one.</p>
 *
 * <p>Then getAccessToken's `if (!account) return null` answers every later call at once, for the rest
 * of this visit and for every visit after it — a browser that never signed in behaves exactly this
 * way, and it is the state this one is actually in.</p>
 *
 * <p>It also settles what the UI says. getAccount() is what ProfileDropdown and SummaryStep's
 * isSignedIn read, so a record left in place shows a signed-in profile menu to someone the server is
 * answering as anonymous. Removing it makes both halves agree.</p>
 */
async function forget(client: PublicClientApplication, account: AccountInfo): Promise<boolean> {
  try {
    await client.clearCache({ account });
    console.warn("[auth] signed-in session no longer exists at B2C; cleared the stale account", account.username);
    return true;
  } catch (e) {
    console.error("[auth] could not clear the stale account", e);
    return false;
  }
}
